"use client";

import { cn } from "@repo/utils/cn";
import { Text, text } from "./Text";
import { CrossIcon } from "./icons/functional/CrossIcon";
import { ComponentProps } from "react";
import { SelectOption } from "./Select";

type InputFieldState = "default" | "error" | "disabled";

export type InputFieldProps = {
  mode?: "default" | "bright";
  type: ComponentProps<"input">["type"];
  name: ComponentProps<"input">["name"];
  label: string;
  // Accessible name for the input when `label` is intentionally empty and the
  // visible label lives on a sibling control (e.g. the phone country select).
  ariaLabel?: string;
  options?: SelectOption[];
  value?: string;
  defaultCountryCode?: string;
  state?: InputFieldState;
  errorMessage?: string;
  onChange?: (value: string) => void;
} & Omit<ComponentProps<"div">, "title" | "onChange">;

export function InputField({
  mode = "default",
  type,
  name,
  label,
  ariaLabel,
  value = "",
  state = "default",
  errorMessage,
  onChange,
  ...props
}: InputFieldProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div className={"ui:flex ui:flex-col ui:gap-2"} {...props}>
      <label className="ui:group ui:relative ui:mb-tiny ui:flex ui:h-[64px] ui:flex-col ui:justify-center ui:pt-5.5">
        <span
          className={cn(
            "ui:absolute ui:left-0",
            "ui:transition-all",
            "ui:text-body ui:font-extrabold ui:tracking-[-0.16px]",
            mode === "bright"
              ? "ui:text-textAmplified"
              : "ui:text-textSecondary",
            "ui:group-focus-within:-translate-y-6.5 ui:group-focus-within:text-micro ui:group-focus-within:font-medium ui:group-focus-within:tracking-[-0.14px]",
            {
              "ui:-translate-y-6.5 ui:text-micro ui:font-medium ui:tracking-[-0.14px]":
                value.length > 0,
              "ui:text-actionIconOutlineDisabled":
                state === "disabled" && mode === "default",
              "ui:text-brand-300": state === "disabled" && mode === "bright",
            },
          )}
        >
          {label}
        </span>

        <input
          type={type}
          name={name}
          aria-label={ariaLabel}
          value={value}
          onChange={handleChange}
          disabled={state === "disabled"}
          className={cn(
            "ui:h-full ui:min-w-full",
            text({ variant: "body", weight: "strong" }),
            "ui:text-textAmplified ui:caret-brand-600 ui:transition-colors",
            "ui:border-0 ui:border-b-2 ui:bg-transparent ui:outline-0",
            {
              "ui:border-borderMuted ui:focus:border-borderAmplified":
                state !== "error" && mode === "default",
              "ui:border-brand-300 ui:focus:border-borderAmplified":
                state !== "error" && mode === "bright",
            },
            "ui:disabled:cursor-default",
            mode === "bright"
              ? "ui:disabled:border-brand-300 ui:disabled:text-brand-300"
              : "ui:disabled:border-actionIconOutlineDisabled ui:disabled:text-actionIconOutlineDisabled",
            {
              "ui:border-functionalNegative": state === "error",
            },
          )}
        />
      </label>

      {state === "error" && (
        <span className={"ui:flex ui:items-center ui:gap-tiny"}>
          <span
            className={
              "ui:flex ui:h-6 ui:w-6 ui:shrink-0 ui:items-center ui:justify-center ui:rounded-full ui:bg-functionalNegative ui:text-neutral-white"
            }
          >
            <CrossIcon className={"ui:h-3.5 ui:w-3.5"} />
          </span>
          <Text
            variant={"micro"}
            className={cn(
              mode === "bright"
                ? "ui:text-textAmplified"
                : "ui:text-textSecondary",
            )}
          >
            {errorMessage}
          </Text>
        </span>
      )}
    </div>
  );
}
